import { getRecordId } from './recordId';

/**
 * App deep-link helpers for in-app navigation and email CTAs.
 *
 * Login redirect is already handled: unauthenticated users hitting a protected
 * path go to `/login?returnUrl=…` and return here after login (see returnUrl.js).
 *
 * Conventions:
 * - Modal/list form pages: `?openRecordId={id}&mode=view|edit`
 * - True detail pages: path params (e.g. `/compliance-orders/:id`)
 */

export const OPEN_RECORD_ID_PARAM = 'openRecordId';
export const OPEN_RECORD_MODE_PARAM = 'mode';

/** Named routes that honor `openRecordId` (+ optional `mode`). */
export const OPEN_RECORD_ROUTES = {
  QI_NOTIFICATION_EXERCISE_LOG: '/qi-notification-exercise-log',
  ANNUAL_TABLETOP_EXERCISE_LOG: '/annual-tabletop-exercise-log',
  RESPONSE_EQUIPMENT_INVENTORY_INSPECTION: '/response-equipment-inventory-inspection',
  EQUIPMENT_DEPLOYMENT_EXERCISE_LOG: '/equipment-deployment-exercise-log',
  ENGINES: '/engines',
  AUTHORIZED_WASTE_RECEIPT: '/authorized-waste-receipt',
  INVOICING: '/invoicing',
  WEEKLY_PWS_WATER_MONITORING: '/weekly-pws-water-monitoring',
  MONTHLY_PWS_WATER_SAMPLE: '/monthly-pws-water-sample',
  MONTHLY_PWS_ANALYTICAL_REPORT_REVIEW: '/monthly-pws-analytical-report-review',
  QUARTERLY_DLQOR: '/quarterly-dlqor',
  WEEKLY_RECLAIMED_WATER_MONITORING: '/weekly-reclaimed-water-monitoring',
  MONTHLY_RECLAIMED_WATER_SAMPLE: '/monthly-reclaimed-water-sample',
  MONTHLY_RECLAIMED_ANALYTICAL_REPORT_REVIEW: '/monthly-reclaimed-analytical-report-review',
  SEMI_ANNUAL_RECLAIMED_WATER_SAMPLE: '/semi-annual-reclaimed-water-sample',
  SEMI_ANNUAL_RECLAIMED_ANALYTICAL_REPORT_REVIEW: '/semi-annual-reclaimed-analytical-report-review',
  ANNUAL_RECLAIMED_WATER_SAMPLE: '/annual-reclaimed-water-sample',
  ANNUAL_RECLAIMED_ANALYTICAL_REPORT_REVIEW: '/annual-reclaimed-analytical-report-review',
};

/**
 * SQL / API table name → app route (keep in sync with backend appDeepLinks + navigationAccess).
 */
export const FORM_TABLE_ROUTES = {
  PWSWeeklyWaterSample: '/weekly-pws-water-monitoring',
  PWSMonthlyWaterSample: '/monthly-pws-water-sample',
  MonthlyPWSAnalytical: '/monthly-pws-analytical-report-review',
  QuarterlyDLQOR: '/quarterly-dlqor',
  FlowMeterLogs: '/weekly-flow-meter-logs',
  ColorimeterLogs: '/quarterly-colorimeter-calibration-verification',
  PWSEquipmentServiceLogs: '/weekly-equipment-service-logs',
  ReclaimedWaterWeekly: '/weekly-reclaimed-water-monitoring',
  ReclaimedWaterMonthly: '/monthly-reclaimed-water-sample',
  MonthlyReclaimedAnalytical: '/monthly-reclaimed-analytical-report-review',
  ReclaimedWaterBiannual: '/semi-annual-reclaimed-water-sample',
  BiannualReclaimedAnalytical: '/semi-annual-reclaimed-analytical-report-review',
  ReclaimedWaterAnnual: '/annual-reclaimed-water-sample',
  AnnualReclaimedAnalytical: '/annual-reclaimed-analytical-report-review',
  AuthorizedWasteReceipts: '/authorized-waste-receipt',
  AuthorizedWasteReceipt: '/authorized-waste-receipt',
  CustomForm: '/custom-form-entry',
  CustomFormRecords: '/custom-form-entry',
  Engines: '/engines',
};

/** Detail routes that load a record from the path (no query needed). */
export const DETAIL_ROUTES = {
  projectWorkspace: (projectId) => `/project-workspace/${encodeURIComponent(String(projectId))}`,
  complianceOrder: (orderId) => `/compliance-orders/${encodeURIComponent(String(orderId))}`,
  auditDetail: (masterId) => `/audit-detail/${encodeURIComponent(String(masterId))}`,
};

/**
 * RecordFormModal form type → list page that honors `openRecordId`.
 * PWS / reclaimed / custom forms also resolve through FORM_TABLE_ROUTES.
 */
export const RECORD_FORM_LIST_ROUTES = {
  ComplianceRecord: '/compliance',
  DrainageRecordation: '/drainage-recordation',
  SPCCAnnual: '/spcc-annual-inspection',
  SPCCRoutine: '/spcc-routine-inspection',
  EnvironmentalSampling: '/environmental-sampling',
  EnvironmentalLabAnalysis: '/environmental-lab-analysis',
  SWPPP_Quarterly: '/swppp-quarterly-inspection',
  SWPPP_Discharge_Monitoring: '/swppp-discharge-monitoring-inspection',
  SumpPumpMaintenance: '/sump-pump-maintenance-inspection',
  TransferHoseAndPipeTesting: '/transfer-hose-and-pipe-testing',
  HazardousWasteRemoval: '/hazardous-waste-removal',
  AirGlycolLog: '/air-glycol-pump-log',
  AirOpacityLog: '/air-opacity-monitoring',
  QI_Notification_Ex_Logs: '/qi-notification-exercise-log',
  Tabletop_Annual_Ex_Logs: '/annual-tabletop-exercise-log',
  Response_Equipment_Inventory_Inspections: '/response-equipment-inventory-inspection',
  Equipment_Deployment_Ex_Logs: '/equipment-deployment-exercise-log',
  CustomForm: '/custom-form-entry',
  Invoice: '/invoicing',
  Engines: '/engines',
};

/**
 * Numeric primary key safe to put in a shared URL.
 * Skips composite grid keys such as `Compliance-123`.
 * @param {object} record
 * @returns {string|null}
 */
export function resolveShareRecordId(record) {
  if (!record || typeof record !== 'object') return null;
  const candidates = [
    getRecordId(record),
    record.recordId,
    record.RecordId,
    record.complianceId,
    record.ComplianceId,
    record.MasterID,
    record.masterId,
  ];
  for (const value of candidates) {
    if (value == null || value === '') continue;
    const text = String(value).trim();
    if (/^\d+$/.test(text)) return text;
  }
  return null;
}

/**
 * @param {string} [mode]
 * @returns {'view'|'edit'}
 */
export function normalizeOpenRecordMode(mode) {
  return String(mode || 'view').toLowerCase() === 'edit' ? 'edit' : 'view';
}

/**
 * Ensure a path starts with a single leading slash (relative app path).
 * @param {string} path
 * @returns {string}
 */
export function normalizeAppPath(path) {
  if (!path || typeof path !== 'string') return '/';
  const trimmed = path.trim();
  if (!trimmed) return '/';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const url = new URL(trimmed);
      return `${url.pathname}${url.search}${url.hash}` || '/';
    } catch {
      return '/';
    }
  }
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withSlash.replace(/^\/{2,}/, '/');
}

/**
 * @param {string} tableName
 * @returns {string|null}
 */
export function getRouteForFormTable(tableName) {
  if (!tableName) return null;
  const key = String(tableName).trim();
  if (FORM_TABLE_ROUTES[key]) return FORM_TABLE_ROUTES[key];
  const lower = key.toLowerCase();
  const match = Object.keys(FORM_TABLE_ROUTES).find((k) => k.toLowerCase() === lower);
  return match ? FORM_TABLE_ROUTES[match] : null;
}

/**
 * Build a relative path that opens a record on a list/form page.
 * Example: `/qi-notification-exercise-log?openRecordId=123&mode=view`
 *
 * @param {string} routePath - App route (e.g. OPEN_RECORD_ROUTES.QI_NOTIFICATION_EXERCISE_LOG)
 * @param {string|number} recordId
 * @param {'view'|'edit'|string} [mode='view']
 * @returns {string}
 */
export function buildOpenRecordPath(routePath, recordId, mode = 'view') {
  if (recordId == null || recordId === '') {
    return normalizeAppPath(routePath);
  }
  const base = normalizeAppPath(routePath).split('?')[0];
  const params = new URLSearchParams();
  params.set(OPEN_RECORD_ID_PARAM, String(recordId));
  params.set(OPEN_RECORD_MODE_PARAM, normalizeOpenRecordMode(mode));
  return `${base}?${params.toString()}`;
}

/**
 * @param {string} tableName
 * @param {string|number} recordId
 * @param {'view'|'edit'|string} [mode='view']
 * @returns {string|null}
 */
export function buildOpenRecordPathForTable(tableName, recordId, mode = 'view') {
  const route = getRouteForFormTable(tableName);
  if (!route) return null;
  return buildOpenRecordPath(route, recordId, mode);
}

/**
 * App-relative path that opens this record. Detail pages use a path id.
 * List forms use `?openRecordId=&mode=`.
 * @param {string} formType - RecordFormModal form type
 * @param {object} record
 * @param {'view'|'edit'|string} [mode='view']
 * @returns {string|null}
 */
export function buildRecordSharePath(formType, record, mode = 'view') {
  const recordId = resolveShareRecordId(record);
  if (!formType || recordId == null) return null;

  if (formType === 'ComplianceOrder') {
    return DETAIL_ROUTES.complianceOrder(recordId);
  }
  if (formType === 'ComplianceVerificationRecord') {
    return DETAIL_ROUTES.auditDetail(recordId);
  }

  const route = RECORD_FORM_LIST_ROUTES[formType] || getRouteForFormTable(formType);
  if (!route) return null;
  return buildOpenRecordPath(route, recordId, mode);
}

/**
 * Absolute URL for the current origin, suitable to paste into email or Teams.
 * @param {string} formType
 * @param {object} record
 * @param {'view'|'edit'|string} [mode='view']
 * @returns {string|null}
 */
export function buildRecordShareUrl(formType, record, mode = 'view') {
  const path = buildRecordSharePath(formType, record, mode);
  if (!path || typeof window === 'undefined') return null;
  return `${window.location.origin}${path}`;
}

/**
 * @param {string|URLSearchParams} searchOrParams - `location.search` or URLSearchParams
 * @returns {{ openRecordId: string, mode: 'view'|'edit' } | null}
 */
export function parseOpenRecordSearch(searchOrParams) {
  const params =
    searchOrParams instanceof URLSearchParams
      ? searchOrParams
      : new URLSearchParams(
          typeof searchOrParams === 'string' && searchOrParams.startsWith('?')
            ? searchOrParams.slice(1)
            : searchOrParams || ''
        );

  const openRecordId = params.get(OPEN_RECORD_ID_PARAM);
  if (!openRecordId) return null;

  return {
    openRecordId: String(openRecordId),
    mode: normalizeOpenRecordMode(params.get(OPEN_RECORD_MODE_PARAM)),
  };
}

/**
 * Find a record in a loaded list by openRecordId.
 * @param {Array<object>} records
 * @param {string|number} openRecordId
 * @param {(record: object) => string|number|null|undefined} [getId]
 * @returns {object|undefined}
 */
export function findRecordByOpenId(records, openRecordId, getId = (record) => record?.id ?? record?.Id) {
  if (!Array.isArray(records) || openRecordId == null) return undefined;
  const target = String(openRecordId);
  return records.find((record) => String(getId(record) ?? '') === target);
}
